import axios from "axios"
import { useTranslation } from "react-i18next"
import React, { useEffect, useState } from "react"

import Button from "@material-ui/core/Button"
import DialogMui from "@material-ui/core/Dialog"
import DialogActions from "@material-ui/core/DialogActions"
import DialogContent from "@material-ui/core/DialogContent"
import Box from "@material-ui/core/Box"
import DialogContentText from "@material-ui/core/DialogContentText"

import { DialogTitle } from "components/partials"
import { AxiosHelper } from "common/helpers"
import { DangerButton, SuccessButton, Alert } from "components/widgets"

const AdminFormDialog = props => {
    const {
        FormComponent,
        DialogProps,
        DialogTitleProps,
        DeleteButtonProps,
        SubmitButtonProps,
        AdditionalButton
    } = props
    const { t } = useTranslation()
    const [formData, setFormData] = useState({})
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState(null)

    const [isFormChanged, setIsFormChanged] = useState(false)
    const [isDiscardConfirmationOpen, setIsDiscardConfirmationOpen] = useState(
        false
    )

    useEffect(() => {
        setError(null)
        setFormData(props.formData)
    }, [props.formData])

    function closeDialog() {
        DialogProps.onClose({}, "backdropClick")
    }

    function onClose() {
        if (props.shouldPromptOnCancel && isFormChanged) {
            setIsDiscardConfirmationOpen(true)
            return
        }

        closeDialog()
    }

    function onFormChange(key, value) {
        setFormData({ ...formData, [key]: value })
        setError(prevState => {
            if (prevState === null) return prevState
            if (typeof prevState === "string") return prevState
            return { ...prevState, [key]: undefined }
        })
    }

    async function onSubmit(event) {
        event.preventDefault()

        if (!props.submitCallback) return
        await submitForm(props.submitCallback)
    }

    async function submitForm(callback) {
        setIsLoading(true)
        let hasError = false

        try {
            await callback(formData)
        } catch (error) {
            hasError = true

            if (!axios.isAxiosError(error)) {
                setError(error.message)
            } else {
                if (error.response?.status === 403) {
                    setError(t("errors.insufficientPermission"))
                } else {
                    console.log("error:", error)
                    const parsed = AxiosHelper.parseError(error)
                    if (typeof parsed === "string") {
                        setError(parsed)
                    } else {
                        if (Object.keys(parsed).length) {
                            setError(parsed)
                        } else {
                            setError(t("errors.somethingWentWrong"))
                        }
                    }
                }
            }
        }

        setIsLoading(false)

        if (!hasError) {
            closeDialog()
        }
    }

    function onClickDelete() {
        if (props.onClickDelete) {
            props.onClickDelete(formData)
        }
    }

    function onCloseDiscardConfirmation() {
        setIsDiscardConfirmationOpen(false)
    }

    function onChangeForm() {
        setIsFormChanged(true)
    }

    const isErrorString = typeof error === "string"

    const { getPropsFromFormData, ...SubmitButtonPropsRest } =
        SubmitButtonProps || {}
    const submitPropsFromFormData =
        getPropsFromFormData && getPropsFromFormData(formData)
    const disableSubmit =
        submitPropsFromFormData?.disabled ||
        SubmitButtonProps?.disabled ||
        isLoading

    return (
        <>
            <DialogMui
                PaperProps={{
                    style: {
                        width: "100%",
                        maxWidth: "750px"
                    }
                }}
                {...DialogProps}
            >
                <DialogTitle onClose={onClose} {...DialogTitleProps} />

                <DialogContent>
                    {isErrorString && (
                        <Alert severity="error" marginBottom={2}>
                            {error}
                        </Alert>
                    )}
                    <FormComponent
                        formData={formData}
                        error={isErrorString ? null : error}
                        onFormChange={onFormChange}
                        setFormData={setFormData}
                        setError={setError}
                        formProps={{
                            id: "admin-form",
                            onSubmit,
                            onChange: onChangeForm
                        }}
                    />
                </DialogContent>

                <DialogActions>
                    {props.onClickDelete && (
                        <DangerButton
                            {...DeleteButtonProps}
                            style={{ marginRight: "auto" }}
                            onClick={onClickDelete}
                            variant="outlined"
                        >
                            {t("view.admin.deleteRow")}
                        </DangerButton>
                    )}

                    <Box
                        style={{
                            display: "flex",
                            columnGap: 8
                        }}
                    >
                        <Box display="inline-block">
                            <Button onClick={onClose}>{t("view.admin.cancel")}</Button>
                        </Box>
                        <SuccessButton
                            type="submit"
                            form="admin-form"
                            variant="contained"
                            {...SubmitButtonPropsRest}
                            {...submitPropsFromFormData}
                            disabled={disableSubmit}
                        >
                            {t("view.admin.saveRow")}
                        </SuccessButton>

                        {AdditionalButton && (
                            <AdditionalButton
                                submitForm={submitForm}
                                formData={formData}
                                disabled={disableSubmit}
                            />
                        )}
                    </Box>
                </DialogActions>
            </DialogMui>

            <CloseConfirmation
                onOk={closeDialog}
                DialogProps={{
                    open: isDiscardConfirmationOpen,
                    onClose: onCloseDiscardConfirmation
                }}
            />
        </>
    )
}

const CloseConfirmation = props => {
    const { DialogProps } = props
    const { t } = useTranslation()

    function onOk() {
        if (props.onOk) {
            onClose()
            props.onOk()
        }
    }

    function onClose() {
        if (DialogProps.onClose) {
            DialogProps.onClose({}, "backdropClick")
        }
    }

    return (
        <DialogMui {...DialogProps}>
            <DialogTitle onClose={onClose}>
                {t("view.admin.discardChangesModalTitle")}
            </DialogTitle>

            <DialogContent>
                <DialogContentText>
                    {t("view.admin.discardChangesModalBody")}
                </DialogContentText>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>{t("view.admin.cancel")}</Button>
                <DangerButton variant="outlined" onClick={onOk}>
                    {t("view.admin.discardChangesModalContinue")}
                </DangerButton>
            </DialogActions>
        </DialogMui>
    )
}

export default AdminFormDialog
